'use client';
import { useState, useMemo } from 'react';
import Link from 'next/link';
import { marked } from 'marked';
import { trackFaqExpand } from '@/lib/analytics';

// Configure marked for GFM (tables, strikethrough, etc.)
marked.setOptions({ gfm: true, breaks: false });

export interface FAQItem {
    question: string;
    answer: string;
    relatedBlogPost?: string; // Blog post slug for "Read More" link
}
interface FAQSectionProps {
    items: FAQItem[];
    toolName?: string;
    spacingClassName?: string;
}
export default function FAQSection({ items, toolName, spacingClassName }: FAQSectionProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    // Pre-render all FAQ markdown to HTML once on mount — avoids heavy
    // react-markdown parsing on every accordion toggle (fixes 7,968ms INP)
    const renderedAnswers = useMemo(() =>
        items?.map(item => marked.parse(item.answer) as string) ?? [],
        [items]
    );

    if (!items || items.length === 0) {
        return null;
    }
    // NOTE: FAQPage schema is handled by the parent Layout component (ToolLayout, ServiceLayout, IndustryLayout)
    // to avoid duplicate FAQPage schema errors in Google Search Console
    const spacingClasses = spacingClassName ?? 'mt-16 pt-12';
    return (<>

      {/* FAQ Section */}
      <div className={`${spacingClasses} border-t-2 border-gray-300 dark:border-gray-600`}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2" id="frequently-asked-questions">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {toolName
            ? `Common questions about the ${toolName}`
            : 'Find answers to common questions'}
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4">
            {items.map((item, index) => (<div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:border-blue-300 transition-colors">
                {/* Question Button */}
                <button onClick={() => {
                  const isOpening = openIndex !== index;
                  setOpenIndex(isOpening ? index : null);
                  if (isOpening) {
                    trackFaqExpand(item.question, typeof window !== 'undefined' ? window.location.pathname : '');
                  }
                }} className="w-full px-6 py-4 text-left flex items-center justify-between bg-gray-50 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-950/40 active:bg-blue-100 dark:active:bg-blue-900/40 transition-colors cursor-pointer" id={`faq-question-${index}`} aria-expanded={openIndex === index} aria-controls={`faq-answer-${index}`}>
                  <span className="font-semibold text-gray-900 dark:text-gray-100 pr-4">
                    {item.question}
                  </span>
                  <svg className={`w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
                  </svg>
                </button>

                {/* Answer - Always in DOM for SEO, animated with grid-template-rows */}
                <div
                  id={`faq-answer-${index}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                  className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                    openIndex === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className={`px-6 py-4 bg-white dark:bg-gray-800 ${openIndex === index ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}>
                      <div
                        className="prose prose-sm md:prose-base text-gray-700 dark:text-gray-300 max-w-none"
                        suppressHydrationWarning
                        dangerouslySetInnerHTML={{ __html: renderedAnswers[index] }}
                      />
                      {item.relatedBlogPost && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                          <Link
                            href={item.relatedBlogPost.startsWith('/') ? item.relatedBlogPost : `/blog/${item.relatedBlogPost}`}
                            className="inline-flex items-center gap-2 text-[#2813e8] hover:text-[#1f0fb8] font-semibold transition-colors"
                          >
                            <span>Read More</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>))}
          </div>
        </div>
      </div>
    </>);
}
